/**
 * tokens-writer — writes BrandVariable edits from the modal back into
 * `.appostle/brand/tokens.json` (W3C DTCG format).
 *
 * This is the write-side counterpart of tokens-adapter.ts. The adapter
 * flattens tokens.json into BrandEntry/BrandVariable shape; this module
 * performs the inverse: given a list of updated BrandVariables, it patches
 * the in-memory token tree and atomically writes it back.
 *
 * Design decisions:
 * - Only $value and Appostle extension fields (weight, enabled, locked) are
 *   ever touched. $type, $description, $extensions from other namespaces,
 *   and all structural nodes are preserved as-is.
 * - Alias preservation is OUT OF SCOPE for v1. If a token had
 *   $value: "{color.primary}" and the user edits it, the alias is replaced
 *   with the concrete value. To preserve an alias, edit tokens.json directly.
 * - Synthetic weight keys (e.g. "color.primary.weight") are written back
 *   into the parent token's $extensions rather than as child nodes in the
 *   JSON tree.
 * - Write is atomic: tokens.json.tmp is written first, then renamed over the
 *   original. If the write fails mid-way, the original file is intact.
 */

import fs from "node:fs/promises";
import type { BrandFrontmatter } from "./scanner.js";
import { tokensJsonPath } from "./tokens-adapter.js";

interface TokenExtensionsAppostle {
  weight?: number;
  locked?: boolean;
  enabled?: boolean;
  options?: string[];
  fileRef?: string;
  fontProvider?: string;
}

interface TokenNode {
  $type?: string;
  $value?: unknown;
  $description?: string;
  $extensions?: {
    ohlord?: {
      appostle?: TokenExtensionsAppostle;
    };
  };
  [child: string]: unknown;
}

/**
 * Navigates a dot-separated path in the token tree and returns the leaf node,
 * or null if any segment is missing or not an object.
 */
function navigatePath(root: Record<string, unknown>, dotPath: string): TokenNode | null {
  const parts = dotPath.split(".");
  let cursor: unknown = root;
  for (const part of parts) {
    if (!cursor || typeof cursor !== "object" || Array.isArray(cursor)) return null;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  if (!cursor || typeof cursor !== "object" || Array.isArray(cursor)) return null;
  return cursor as TokenNode;
}

/**
 * Deletes a node at a dot-separated path from the token tree. No-op when
 * any segment along the path is missing — a doomed delete request is the
 * same outcome as the entry already being gone. Only the leaf node is
 * removed; ancestor groups (e.g. `color`) are preserved even if they end
 * up empty so the rest of the schema remains intact.
 */
function deletePath(root: Record<string, unknown>, dotPath: string): void {
  const parts = dotPath.split(".");
  if (parts.length === 0) return;
  let cursor: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const next = cursor[parts[i]!];
    if (!next || typeof next !== "object" || Array.isArray(next)) return;
    cursor = next as Record<string, unknown>;
  }
  const leaf = parts[parts.length - 1]!;
  delete cursor[leaf];
}

/**
 * Dynamic palette entries live under `color.<slug>` where `<slug>` is a
 * kebab-case identifier authored by the user (no nested groups, no further
 * dots in the slug). The colors page can add new palette entries on the fly,
 * so the writer must upsert these — creating a fresh leaf node when the path
 * doesn't exist yet, instead of silently skipping like for fixed token paths.
 *
 * Returns the (possibly newly-created) leaf node, or null when the variable
 * doesn't match the dynamic `color.<slug>` shape (in which case the caller
 * should fall through to the normal navigate-only path).
 */
function ensureColorPaletteEntry(
  root: Record<string, unknown>,
  key: string,
  varType: string | undefined,
): TokenNode | null {
  if (varType !== "color" && varType !== "gradient") return null;
  const parts = key.split(".");
  if (parts.length !== 2 || parts[0] !== "color") return null;
  const slug = parts[1];
  if (!slug) return null;

  const colorNs = root.color;
  if (!colorNs || typeof colorNs !== "object" || Array.isArray(colorNs)) return null;
  const ns = colorNs as Record<string, unknown>;

  const existing = ns[slug];
  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
    return existing as TokenNode;
  }

  // Brand-new palette entry — create a minimal leaf carrying the right $type.
  const created: TokenNode = {
    $type: varType,
    $value: "",
  };
  ns[slug] = created;
  return created;
}

/**
 * Ensures the Appostle extensions object exists on a token node and returns
 * a mutable reference to it.
 */
/**
 * Maps the client-side BrandVariable type to a W3C DTCG `$type` string. Used
 * when materialising a brand-new leaf node from a modal edit (e.g. a freshly
 * declared `token.h1.textTransform`) so the on-disk shape is correct and the
 * adapter reads it back with the right BrandVariable type.
 */
function brandVariableTypeToDtcg(type: string | undefined): string {
  switch (type) {
    case "color":
      return "color";
    case "gradient":
      return "gradient";
    case "font":
      return "fontFamily";
    case "asset":
      return "asset";
    case "number":
      return "number";
    default:
      return "string";
  }
}

function ensureAppostleExtensions(node: TokenNode): TokenExtensionsAppostle {
  node.$extensions ??= {};
  const ext = node.$extensions as Record<string, unknown>;
  ext.ohlord ??= {};
  const ohlord = ext.ohlord as Record<string, unknown>;
  ohlord.appostle ??= {};
  return ohlord.appostle as TokenExtensionsAppostle;
}

/**
 * Writes updated BrandVariable values back into tokens.json atomically.
 *
 * Variables whose key ends with ".weight" are treated as synthetic extension
 * fields (synthesized by the adapter from color token metadata) and are
 * written back to the parent token's Appostle extensions rather than as child
 * nodes in the JSON tree.
 *
 * All other variables are written as $value updates on the token node at
 * the corresponding dot-path in the tree.
 */
export async function writeBrandTokens(
  frontmatter: BrandFrontmatter,
  workspaceRoot: string,
): Promise<void> {
  const filePath = tokensJsonPath(workspaceRoot);
  const tmpPath = `${filePath}.tmp`;

  let text: string;
  try {
    text = await fs.readFile(filePath, "utf8");
  } catch (err) {
    throw new Error(
      `Failed to read tokens.json: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  let root: Record<string, unknown>;
  try {
    root = JSON.parse(text) as Record<string, unknown>;
  } catch (err) {
    throw new Error(
      `tokens.json is not valid JSON: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  // Apply explicit deletes first — variables in the patch list might
  // reference paths the user just removed (e.g. the synthesized .weight
  // sibling of a deleted palette entry); processing deletes up front
  // means those synthesized writes naturally no-op against missing nodes.
  const removedKeys = frontmatter.removedKeys ?? [];
  for (const key of removedKeys) {
    deletePath(root, key);
  }

  const variables = frontmatter.variables ?? [];

  for (const variable of variables) {
    const { key, value } = variable;

    // Synthetic extension: color.primary.weight → write to color.primary's extensions
    if (key.endsWith(".weight")) {
      const parentKey = key.slice(0, -".weight".length);
      const parentNode = navigatePath(root, parentKey);
      if (parentNode && parentNode.$value !== undefined) {
        const appostle = ensureAppostleExtensions(parentNode);
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) appostle.weight = parsed;
      }
      continue;
    }

    // Dynamic palette entries (color.<slug>) are upserted — the colors page
    // adds new swatches on the fly, so a missing path is a "create", not a
    // "skip". For every other key shape, fall through to the strict navigate
    // path.
    const upserted = ensureColorPaletteEntry(root, key, variable.type);
    let node = upserted ?? navigatePath(root, key);
    let created = upserted !== null;

    // Token-group fields (token.<group>.<field>) are upserted when the parent
    // group exists. This covers schema fields that newer Appostle versions
    // declare but the on-disk file (seeded from an older template) hasn't
    // materialised yet — e.g. textTransform on h1-h6/body-*/label. Without
    // this, the modal change is written nowhere and a refetch reverts the UI.
    if (!node || node.$value === undefined) {
      const parts = key.split(".");
      if (parts.length >= 2) {
        const parentPath = parts.slice(0, -1).join(".");
        const leafName = parts[parts.length - 1]!;
        const parent = navigatePath(root, parentPath);
        if (parent && parent.$value === undefined) {
          const dtcgType = brandVariableTypeToDtcg(variable.type);
          const fresh: TokenNode = { $type: dtcgType, $value: "" };
          (parent as Record<string, unknown>)[leafName] = fresh;
          node = fresh;
          created = true;
        }
      }
    }

    if (!node || (!created && node.$value === undefined)) {
      // Path's parent group doesn't exist either — skip silently.
      continue;
    }

    node.$value = value;

    // Palette entries can morph between solid color and gradient via the
    // picker (single component, two modes). Keep the on-disk `$type` in
    // sync with the BrandVariable type so reads after a morph render the
    // right swatch. Only touched for palette `color.<slug>` entries so
    // other token shapes (e.g. `token.bg-base`) keep their authored type.
    if (
      upserted !== null &&
      (variable.type === "color" || variable.type === "gradient") &&
      node.$type !== variable.type
    ) {
      node.$type = variable.type;
    }

    // Update Appostle extension fields when the BrandVariable carries them
    const appostle = ensureAppostleExtensions(node);
    if (variable.locked !== undefined) appostle.locked = variable.locked;
    if (variable.enabled !== undefined) appostle.enabled = variable.enabled;
    // fileRef / fontProvider must round-trip through tokens.json — dropping
    // them here silently detaches font/asset variables from their files, so
    // a picked font reverts to name-only resolution after the next reload.
    if (variable.fileRef !== undefined) appostle.fileRef = variable.fileRef;
    if (variable.fontProvider !== undefined) appostle.fontProvider = variable.fontProvider;
  }

  // Atomic write: write to .tmp first, then rename over the original
  const serialized = JSON.stringify(root, null, 2);
  try {
    await fs.writeFile(tmpPath, serialized, "utf8");
    await fs.rename(tmpPath, filePath);
  } catch (err) {
    // Best-effort cleanup of the tmp file
    try {
      await fs.unlink(tmpPath);
    } catch {
      // Ignore cleanup errors
    }
    throw new Error(
      `Failed to write tokens.json: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}
